import { ProjektListenTyp, SpsListenTyp } from './sps-listen-typ.model';
import { SpsAnlagenteilSpalte, SpsImportListe } from './sps-import.model';
import { SpsFortschritt, SpsPruefstatus } from './sps-signal.model';

/*
 * Spiegelbild der Backend-DTOs aus Models_SpsUpdateDtos.cs: Listenupdate
 * (neue Excel-Version derselben Liste) und das Löschen einzelner Einträge
 * bzw. Signale. Enums kommen als Klartext.
 */

// =========================================================
// LISTENUPDATE  (POST api/sps/listen/{id}/update/vorschau, POST …/update)
// =========================================================

export interface SpsListenUpdateFeld {
  feld: string;
  alt: string | null;
  neu: string | null;
}

export interface SpsListenUpdateEintrag {
  /** Bestehender Eintrag; null bei einer neuen Zeile. */
  eintragId: number | null;
  alteZeile: number | null;
  /** Zeile in der NEUEN Datei; null bei einer gelöschten Zeile. */
  neueZeile: number | null;
  signalname: string | null;
  adresseOriginal: string | null;
  stationsName: string | null;
  beschreibung: string | null;
  signalId: number | null;
  pruefstatus: SpsPruefstatus | null;
  /** Signal geprüft (Status, Protokoll oder Bemerkung vorhanden). */
  geprueft: boolean;
  /** Das Signal hat weitere Quellen (andere Liste) und bleibt beim Löschen bestehen. */
  signalHatWeitereQuellen: boolean;
  /** Zeile wurde zuvor nur aus der Prüfung entfernt – wird aktualisiert, aber nicht erneut geprüft. */
  ausPruefungEntfernt: boolean;
  aenderungen: SpsListenUpdateFeld[];
  /** Änderung an prüfrelevanten Feldern eines geprüften Signals → „Seit Prüfung geändert“. */
  pruefrelevant: boolean;
  anlagenteilAlt: string | null;
  anlagenteilNeu: string | null;
  hinweis: string | null;
}

/** Zeilen, die nicht eindeutig zuzuordnen sind – werden weder aktualisiert noch gelöscht noch angelegt. */
export interface SpsListenUpdateKonflikt {
  schluessel: string;
  grund: string;
  bestand: SpsListenUpdateEintrag[];
  neueZeilen: SpsListenUpdateEintrag[];
}

/** Vorschau eines Listenupdates – ändert nichts. Die Zähler gelten immer für alles, die Listen sind auf 300 Einträge begrenzt. */
export interface SpsListenUpdateVorschau {
  listeId: number;
  projektId: number;
  name: string;
  listenTyp: ProjektListenTyp;
  bisherigeDatei: string;
  neueDatei: string;
  blatt: string | null;
  blaetter: string[];
  /** False, wenn Pflichtspalten fehlen – dann ist kein Update möglich. */
  typPasst: boolean;
  warnungen: string[];
  zeilenGesamt: number;
  gueltig: number;
  ungueltig: number;
  bestandEintraege: number;
  unveraendert: number;
  aktualisiert: number;
  neu: number;
  geloescht: number;
  konflikte: number;
  /** Neue Zeilen, die zuvor manuell gelöscht wurden – nur mit ausdrücklicher Option wiederhergestellt. */
  zuvorManuellGeloescht: number;
  geprueftAktualisiert: number;
  geprueftSeitPruefungGeaendert: number;
  geprueftGeloescht: number;
  geprueftGeloeschtMitWeiterenQuellen: number;
  geprueftGeloeschtArchiviert: number;
  gepruefteBetroffen: number;
  signaleUngeprueftGeloescht: number;
  anlagenteilSpalte: SpsAnlagenteilSpalte | null;
  anlagenteileNeu: number;
  anlagenteilZuordnungenGeaendert: number;
  aktualisierte: SpsListenUpdateEintrag[];
  neue: SpsListenUpdateEintrag[];
  geloeschte: SpsListenUpdateEintrag[];
  zuvorGeloeschte: SpsListenUpdateEintrag[];
  konfliktListe: SpsListenUpdateKonflikt[];
  ohneUnterschied: boolean;
}

export interface SpsListenUpdateErgebnis {
  liste: SpsImportListe;
  unveraendert: number;
  aktualisiert: number;
  neu: number;
  geloescht: number;
  konflikte: number;
  uebersprungen: number;
  wiederhergestellt: number;
  geprueftErhalten: number;
  seitPruefungGeaendert: number;
  signaleErhalten: number;
  signaleGeloescht: number;
  signaleArchiviert: number;
  signaleNeuVerknuepft: number;
  anlagenteileNeu: number;
  anlagenteilZuordnungenGeaendert: number;
  automatischZusammengefuehrt: number;
  warnungen: string[];
  fortschritt: SpsFortschritt;
}


// =========================================================
// EINZELNE EINTRÄGE UND SIGNALE LÖSCHEN
// =========================================================

/** NurAusPruefung: Herkunftszeilen bleiben; Vollstaendig: Signal samt Verlauf und allen Herkunftszeilen löschen. */
export type SpsSignalLoeschModus = 'NurAusPruefung' | 'Vollstaendig';

/** Folge für das Signal, wenn eine Quelle entfernt wird. */
export type SpsSignalFolge = 'KeinSignal' | 'SignalBleibt' | 'SignalArchiviert' | 'SignalGeloescht';

export type SpsLoeschArt = 'NurAusPruefung' | 'Vollstaendig' | 'DurchListenUpdate';

export interface SpsLoeschQuelle {
  eintragId: number;
  listeId: number;
  listeName: string;
  listeTyp: SpsListenTyp;
  excelZeile: number;
  signalname: string | null;
  adresseOriginal: string | null;
  stationsName: string | null;
}

/** Was das Löschen EINER Originalzeile bewirken würde (GET …/eintraege/{id}/loeschvorschau). */
export interface SpsEintragLoeschvorschau {
  eintragId: number;
  listeId: number;
  listeName: string;
  listenTyp: ProjektListenTyp;
  excelZeile: number;
  signalname: string | null;
  adresseOriginal: string | null;
  signalId: number | null;
  signalName: string | null;
  pruefstatus: SpsPruefstatus | null;
  geprueft: boolean;
  folge: SpsSignalFolge;
  weitereQuellen: SpsLoeschQuelle[];
  ausPruefungEntfernt: boolean;
}

/** Was das Löschen eines Signals je Modus bewirken würde (GET api/sps/signale/{id}/loeschvorschau). */
export interface SpsSignalLoeschvorschau {
  signalId: number;
  projektId: number;
  name: string | null;
  adresseNormiert: string | null;
  pruefstatus: SpsPruefstatus;
  geprueft: boolean;
  protokollAnzahl: number;
  quellen: SpsLoeschQuelle[];
  quellenZuordnungsliste: number;
  quellenPlcTags: number;
}

export interface SpsLoeschErgebnis {
  art: SpsLoeschArt;
  eintraegeGeloescht: number;
  eintraegeAusPruefungEntfernt: number;
  quellenGeloest: number;
  signalId: number | null;
  signalFolge: SpsSignalFolge;
  signaleAngepasst: number;
  fortschritt: SpsFortschritt;
}

export function signalFolgeText(folge: SpsSignalFolge): string {
  switch (folge) {
    case 'SignalBleibt': return 'Das Signal bleibt in der Prüfung – es hat weitere Quellen; Prüfstatus und Verlauf bleiben.';
    case 'SignalArchiviert': return 'Das geprüfte Signal verschwindet aus der Prüfung; sein Prüfverlauf bleibt archiviert.';
    case 'SignalGeloescht': return 'Das ungeprüfte Signal wird aus der Prüfung entfernt.';
    default: return 'Die Zeile ist mit keinem Signal verknüpft.';
  }
}
